import { createPrimerCheckoutSession, getPaymentFailUrl, getPaymentSuccessUrl } from "../payments"
import { generateUpsellPageUrl, getUpsellOffers } from "../upsells"
import { getVariantPageUrl } from "../helpers"

export default ({
  upsellOfferType,
  paymentsPath,
  paymentsFailPath,
  paymentsSuccessPath,
  chancePath,
  sendOffPath,
} = {}) => ({
  isLoading: null,
  upsell: null,

  init() {
    this.commonInitUpsell()
  },

  commonInitUpsell() {
    this.store = Alpine.store("data")

    this.upsell = pipelineConfig.offers.find(
      (offer) => offer.type && offer.type.toLowerCase() === upsellOfferType.toLowerCase(),
    )

    if (!this.upsell) return

    this.store.values.activeOffer = this.upsell

    // Remove offer from skipped if user returned
    this.store.values.skippedUpsellOffers = this.store.values.skippedUpsellOffers.filter(
      (type) => type.toLowerCase() !== upsellOfferType.toLowerCase(),
    )
  },

  isTrial(offer = this.activeOffer) {
    return this.store.pricing.isOfferTrial(offer)
  },

  getNextUpsellOfferUrl() {
    const upsellOffers = getUpsellOffers(pipelineConfig.offers)

    const currentUpsellOfferIndex = upsellOffers.findIndex(
      (upsellOffer) => upsellOffer.type.toLowerCase() === upsellOfferType.toLowerCase(),
    )

    let nextUpsellOffer = upsellOffers[currentUpsellOfferIndex + 1]

    // Bundle should be shown only if both Premium and Shopping list upsells are skipped.
    // If either one of them is purchased, bundle should not be offered.
    if (nextUpsellOffer?.type.toLowerCase() === "shopping_list_and_premium") {
      if (
        !this.store.values.skippedUpsellOffers.includes("premium") ||
        !this.store.values.skippedUpsellOffers.includes("shopping_list")
      ) {
        // Skip current next
        nextUpsellOffer = upsellOffers[currentUpsellOfferIndex + 2]
      }
    }

    if (nextUpsellOffer) {
      return generateUpsellPageUrl({
        upsellOfferType: nextUpsellOffer.type,
      })
    } else {
      return this.goToSendOff()
    }
  },

  trackClickPurchase() {
    const trackingClickNames = {
      shopping_list: "Shopping List Accepted Clicked",
      premium: "Premium Accepted Clicked",
      shopping_list_and_premium: "Premium & Shopping List Accepted Clicked",
      chat: "Chat Accepted Clicked",
      basic: "Basic Accepted Clicked",
      workouts: "Workouts Accepted Clicked",
    }

    const upsellType = this.upsell.type.toLowerCase()
    const clickName = trackingClickNames[upsellType] || upsellType
    this.store.internalTracker.trackClick({ clickName })
  },

  trackClickSkip() {
    const trackingClickNames = {
      shopping_list: "Shopping List Rejected Clicked",
      premium: "Premium Rejected Clicked",
      shopping_list_and_premium: "Premium & Shopping List Rejected Clicked",
      chat: "Chat Rejected Clicked",
      basic: "Basic Rejected Clicked",
      workouts: "Workouts Rejected Clicked",
    }

    const upsellType = this.upsell.type.toLowerCase()
    const clickName = trackingClickNames[upsellType] || upsellType
    this.store.internalTracker.trackClick({ clickName })
  },

  trackClickContinue() {
    this.store.internalTracker.trackClick({ clickName: "Continue Clicked" })
  },

  async purchase() {
    this.trackClickPurchase()

    this.isLoading = true

    // Try to purchase with a saved card without redirection to upsell payments page
    try {
      const checkoutSession = await createPrimerCheckoutSession()

      await this.store.trackStatusForAnalytics("INITIATE_CHECKOUT")

      this.isLoading = false

      if (checkoutSession && checkoutSession.instantlyCharged) {
        this.goToThankYou(checkoutSession)
      } else {
        this.goToPayments()
      }
    } catch (e) {
      console.error(e)
      this.goToPaymentsFail()
      return
    }
  },

  async skipCurrentUpsellOffer() {
    this.trackClickSkip()

    // Store skipped upsells to use this data for different upsells pages logic
    this.store.values.skippedUpsellOffers = [
      ...this.store.values.skippedUpsellOffers,
      upsellOfferType.toLowerCase(),
    ]

    // We need skippedOfferId because api-diet depends on it
    if (this.upsell)
      await this.store.push({
        payloadExtraData: { skippedOfferId: this.upsell.brandOfferId },
        isSilentMode: true,
      })

    this.goToNextUpsellOffer()
  },

  goToChance() {
    this.trackClickSkip()
    window.location = getVariantPageUrl({ pagePath: chancePath, pipelineConfig })
  },

  thankYouContinue() {
    this.store.internalTracker.trackClick({ clickName: "Continue Clicked" })
    this.goToNextUpsellOffer()
  },

  //

  goToNextUpsellOffer() {
    const url = this.getNextUpsellOfferUrl()
    if (url) window.location = this.getNextUpsellOfferUrl()
  },

  goToPayments() {
    window.location = getVariantPageUrl({ pagePath: paymentsPath, pipelineConfig })
  },

  goToPaymentsFail() {
    window.location = getPaymentFailUrl({ pagePath: paymentsFailPath })
  },

  goToThankYou(checkoutSession) {
    window.location = getPaymentSuccessUrl({ checkoutSession, pagePath: paymentsSuccessPath })
  },

  goToSendOff() {
    const pagePath = sendOffPath || "/send-off/"
    window.location = getVariantPageUrl({ pagePath, pipelineConfig })
  },

  goToOffer(offerPath) {
    this.store.internalTracker.trackClick({ clickName: "Back Clicked" })

    if (!offerPath) return
    window.location = getVariantPageUrl({ pagePath: offerPath, pipelineConfig })
  },

  // UI

  getPriceRecurring() {
    return this.store.pricing.getPriceRecurring({ offer: this.upsell })
  },

  isPricingRecurring() {
    return this.store.pricing.isPricingRecurring({ offer: this.upsell })
  },

  getPriceNoDiscountRecurring() {
    return this.store.pricing.getPriceNoDiscountRecurring({ offer: this.upsell })
  },

  getPrice() {
    return this.store.pricing.getPrice({ offer: this.upsell })
  },

  getPriceNoDiscount() {
    return this.store.pricing.getPriceNoDiscount({ offer: this.upsell })
  },

  getGoalWeightDiff() {
    const { weightKg, weightLbs, targetWeightKg, targetWeightLbs } = this.store.values

    if (this.store.isMetric()) {
      return weightKg - targetWeightKg
    } else {
      return weightLbs - targetWeightLbs
    }
  },

  getCurrency() {
    return pipelineConfig.currencyCode
  },

  getPriceDataValue(dataKey) {
    return this.store.pricing.getPriceDataValue({ offer: this.upsell, dataKey })
  },

  getPriceDiscount() {
    return this.store.pricing.getPriceDiscount({
      offer: this.upsell,
    })
  },
})
